import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthProvider';
import RequireAuth from '@/auth/RequireAuth';
import AppShell from '@/components/layout/AppShell';
import { LeadsProvider } from '@/leads/LeadsProvider';
import { ConversasProvider } from '@/conversas/ConversasProvider';

import LoginPage from '@/pages/LoginPage';
import InicioPage from '@/pages/InicioPage';
import DashboardPage from '@/pages/DashboardPage';
import ConversasPage from '@/pages/ConversasPage';
import FunilPage from '@/pages/FunilPage';
import LeadsPage from '@/pages/LeadsPage';
import ProximosContatosPage from '@/pages/ProximosContatosPage';
import IndicadoresPage from '@/pages/IndicadoresPage';
import { PRODUTIVIDADE_LIBERADA } from '@/produtividade/disponibilidade';
import RecebimentosEmAbertoPage from '@/pages/RecebimentosEmAbertoPage';
import RecebimentosMesPage from '@/pages/RecebimentosMesPage';
import ImportarRecebimentosPage from '@/pages/ImportarRecebimentosPage';
import ImportacoesPage from '@/pages/ImportacoesPage';
import RemuneracaoPage from '@/pages/RemuneracaoPage';
import ConfigRemuneracaoPage from '@/pages/ConfigRemuneracaoPage';
import MeuExtratoPage from '@/pages/MeuExtratoPage';
import PacientesPage from '@/pages/PacientesPage';
import ConfirmarAgendaPage from '@/pages/ConfirmarAgendaPage';
import AgendaPage from '@/pages/AgendaPage';
import ConfiguracoesPage from '@/pages/ConfiguracoesPage';
import EquipePage from '@/pages/EquipePage';
import PreferenciasPage from '@/pages/PreferenciasPage';
import AdminPage from '@/pages/AdminPage';
import SaudePage from '@/pages/SaudePage';
import { NaoEncontradaPage, SemAcessoPage, SemVinculoPage } from '@/pages/StatusPages';

const VENDAS = ['gestor', 'sdr', 'superadmin'];
const RECEPCAO = ['gestor', 'recepcao', 'superadmin'];
const GESTAO = ['gestor', 'superadmin'];
const TODOS_DA_CLINICA = ['gestor', 'sdr', 'recepcao', 'superadmin'];
/**
 * ★★ WO (2/out/2026) — A PRODUTIVIDADE ATRÁS DE BANDEIRA.
 *
 * Decisão do dono: escondida da equipe até o formato fechar. ★ Com a bandeira em `true` isto
 * vira `VENDAS` e tudo volta ao lote 2 — por isso a volta é de UMA linha, em
 * `produtividade/disponibilidade.js`.
 *
 * ⚠ A guarda da VU lê este arquivo como texto e sabe resolver este ternário: ela importa a
 * bandeira e confere a lista EFETIVA contra o item de menu. Mudar a forma desta linha sem
 * ensinar a guarda a derruba — de propósito.
 */
const PRODUTIVIDADE = PRODUTIVIDADE_LIBERADA ? VENDAS : GESTAO;

/**
 * A árvore de rotas, separada do `BrowserRouter` para o teste de tela conseguir
 * montá-la num `MemoryRouter` (VU: SDR nas URLs escondidas → /sem-acesso).
 */
export function RotasDoApp() {
  return (
    <Routes>
      <Route path="/entrar" element={<LoginPage />} />
      <Route path="/sem-vinculo" element={<SemVinculoPage />} />
      <Route path="/sem-acesso" element={<SemAcessoPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          {/* A raiz é a ÚNICA rota que todo mundo da clínica alcança, e
              não mostra conteúdo: decide para onde cada papel vai.
              Prendê-la a VENDAS barrava a recepcionista logo ao entrar —
              ela caía em /sem-acesso sem ter pedido nada. É também o
              destino do "Voltar ao início" das telas de status. */}
          <Route element={<RequireAuth papeis={TODOS_DA_CLINICA} />}>
            <Route index element={<InicioPage />} />
          </Route>

          {/* Espaço Vendas / SDR — uma única assinatura de leads para
              todas as telas do funil, com os filtros preservados ao
              alternar entre kanban e lista. */}
          <Route element={<RequireAuth papeis={VENDAS} />}>
            <Route element={<LeadsProvider />}>
              <Route path="proximos-contatos" element={<ProximosContatosPage />} />
              <Route path="funil" element={<FunilPage />} />
              <Route path="leads" element={<LeadsPage />} />
              {/* ★★ WG (28/set/2026) — O EXTRATO É DE CADA PESSOA, e agora a SDR
                  alcança o dela. Saiu do bloco `papeis={['gestor']}` para cá.
                  Continua DENTRO do LeadsProvider porque a página usa `useLeads()`.
                  Três camadas garantem que ela só vê o próprio:
                    1. o seletor de pessoa é `{isGestor && …}` (MeuExtratoPage);
                    2. o alvo, para quem não é gestor, é o próprio `uid`;
                    3. ★ o SERVIDOR: `remuneracao/{uid}` e
                       `fechamentos/{mes}/pessoas/{uid}` só abrem para o gestor ou
                       para o dono do uid (medido com papel `sdr`, não com gestor —
                       a regra começa com `isGestor()` e curto-circuitaria).
                  A folha inteira (`/remuneracao`) CONTINUA só do gestor. */}
              <Route path="meu-extrato" element={<MeuExtratoPage />} />
              {/**
                * ★★★ A-7c (13/out/2026) — `/dashboard` VOLTOU PARA O BLOCO DE VENDAS.
                *
                * ★ É a reversão da VU **só para esta rota** (decisão do dono): Dashboard
                * passa a ser de `gestor + sdr + superadmin`, e o **negociador** entra por
                * aqui — ele tem `papel: 'sdr'` e `funcoes: ['negociador']` (WJ), então
                * `VENDAS` já o cobre e **nenhum `funcoes` é preciso**.
                * ⚠⚠ As outras quatro telas escondidas pela VU (`/metas`, `/placar`,
                * `/recebimentos/importar`) **continuam no bloco do gestor** logo abaixo.
                * ⚠⚠⚠ A RECEPÇÃO fica de fora, e não por esquecimento: ela não lê `leads`
                * (`podeVendas` nas rules), então o funil e a grade chegariam vazios — e há
                * teste afirmando que ela não entra.
                * ★★★ E a EDIÇÃO de meta **não** vem com a rota: a grade entra em modo
                * leitura para quem não é gestor (`DashboardPage`, `somenteLeitura`). Era
                * exatamente para este dia que a A-7a pôs a trava dentro da página.
                */}
              <Route path="dashboard" element={<DashboardPage />} />
              {/* ★★ WO lote 2 (02/out/2026) — O PAINEL DIÁRIO, e ele é DA EQUIPE.
                  Fica no bloco `papeis={VENDAS}` (gestor + sdr), não no do gestor: a tela
                  existe para a SDR ver o PRÓPRIO dia. ★ Quem não é gestor vê só a própria
                  linha, e o filtro é por `uid` dentro da página — a rota aberta é de
                  propósito, no molde do `/meu-extrato` da WG.
                  ⚠ A RECEPÇÃO fica de fora: ela não lê `leads` (`podeVendas`) nem as
                  interações por collectionGroup (`papel in ['gestor','sdr']`). Mesma decisão,
                  com as mesmas palavras, do `PlacarPage`.
                  Dentro do LeadsProvider: a página usa `useLeads()` para leads e equipe. */}
              {/* ★★ WO — a bandeira decide QUEM entra, e a recusa é SILENCIOSA.
                  ⚠ NÃO é "/sem-acesso": aquela tela anuncia que existe algo aqui. Decisão do
                  dono — a equipe não deve saber que a seção existe até ser liberada. Quem
                  digitar a URL vai para a tela inicial e pronto, sem faixa e sem texto.
                  ★ `recusaPara` sem `aviso` é exatamente isso. Ver RequireAuth. */}
              <Route element={<RequireAuth papeis={PRODUTIVIDADE} recusaPara="/" />}>
                <Route path="indicadores" element={<IndicadoresPage />} />
                {/* ★★ WO lote 2b — O NOME ANTIGO CONTINUA FUNCIONANDO.
                    A tela chamava-se "Produtividade do dia" e `/produtividade` foi ao ar em
                    2/out; o gestor já a abriu. ⚠ Trocar a URL sem redirecionar quebraria o
                    favorito dele EM SILÊNCIO — e silêncio é o pior modo de quebrar.
                    ★ Fica DENTRO da mesma guarda de propósito: assim a bandeira decide antes,
                    e quem é da equipe cai na tela inicial igual, sem descobrir que a seção
                    existe por causa de um redirecionamento. */}
                <Route path="produtividade" element={<Navigate to="/indicadores" replace />} />
              </Route>
              {/* ★ VU (19/set/2026) — ESCONDIDAS DA EQUIPE, TEMPORARIAMENTE.
                  Dashboard, Metas e vendas, Placar, Meu extrato e Importar
                  Careflow ficam só com o gestor até as telas de meta e
                  comissão estarem conferidas. "Item que a SDR não vê no menu
                  é rota em que ela cai em /sem-acesso" — por isso as rotas
                  desceram para a guarda do gestor junto com os itens do
                  menu. REVERTER = mover as cinco linhas marcadas ★ de volta
                  para o bloco acima (docs/vu-testes.md, COMO REVERTER). As
                  páginas, os testes e as rotas continuam existindo.
                  ★★★ A-7c (13/out/2026): o **Dashboard JÁ FOI REVERTIDO** — subiu para o
                  bloco de VENDAS. ⚠ Sobraram aqui `/metas` e `/placar` (que hoje
                  redirecionam) e `/recebimentos/importar`; `/meu-extrato` saiu na WG. */}
              <Route element={<RequireAuth papeis={['gestor']} />}>
                {/* ★★★ A-7c: `/dashboard` SAIU daqui para o bloco de VENDAS, acima.
                    ⚠ Os redirecionamentos de `/metas` e `/placar` FICAM aqui — ver abaixo. */}
                {/**
                  * ★★★ A-7a (13/out/2026) — `/metas` E `/placar` REDIRECIONAM PARA O DASHBOARD.
                  *
                  * Decisão do dono: a grade diária no fim do Dashboard passou a responder "o
                  * mês contra a meta da clínica", e as metas individuais mudaram para
                  * Indicadores. ★ As duas telas saíram do menu (`oculto: true`) e as rotas
                  * passaram a redirecionar — o gestor já abriu as duas, e **quebrar o favorito
                  * dele em silêncio é o pior modo de quebrar** (a mesma razão de
                  * `/produtividade`).
                  *
                  * ⚠⚠⚠ O `Navigate` fica **DENTRO da guarda do gestor**, de propósito: a SDR
                  * continua caindo em `/sem-acesso` nas duas URLs.
                  *
                  * ★★★ A-7c (13/out/2026) — **A RAZÃO DESTA LINHA MUDOU, e ficou MAIS FORTE.**
                  * ⚠ A versão anterior deste comentário dizia *"se ele subisse um bloco, a SDR
                  * seria redirecionada para `/dashboard` — e ali ela também não entra"*. ★★ Essa
                  * frase **expirou hoje**: `/dashboard` passou a aceitar a SDR. ⚠⚠ Então mover
                  * o `Navigate` para fora **não** daria mais um pingue-pongue inofensivo — daria
                  * à SDR o Dashboard **por uma porta que ninguém abriu** (`/metas`), em silêncio.
                  * ★★★ O caso de `vu.guardas.test.js` que afirma a ESTRUTURA (e não o resultado)
                  * nasceu de uma mutação que sobreviveu **justamente por este motivo**, e é ele
                  * que impede o engano agora. Há teste afirmando as duas metades.
                  *
                  * ★ As páginas `MetasPage` e `PlacarPage` continuam no repositório, sem
                  * nenhum caminho de navegação: ver `docs/a7a-testes.md` (pendência de
                  * limpeza, decisão do dono).
                  */}
                <Route path="metas" element={<Navigate to="/dashboard" replace />} />
                <Route path="placar" element={<Navigate to="/dashboard" replace />} />
                {/* ★ VU (temporário) — VA-2 desenhou importar como tarefa do
                    SDR (é quem tira o relatório do Careflow); enquanto
                    escondido, quem importa é o gestor, e o callable também
                    exige gestor. Dentro do LeadsProvider: o preview oferece
                    a lista de leads para os casamentos que precisam de
                    confirmação. */}
                <Route path="recebimentos/importar" element={<ImportarRecebimentosPage />} />
                {/* VA — ritual mensal do gestor: contratos fechados com saldo.
                    Dentro do LeadsProvider (usa os leads em memória): lançar
                    recebimento é só do gestor, e as rules recusam os demais
                    de qualquer forma. */}
                <Route path="recebimentos" element={<RecebimentosMesPage />} />
                {/* ★ WG: "quem ainda me deve" — a pergunta que /recebimentos
                    respondia até 28/set e que precisou de casa própria quando ela
                    passou a ser a lista do mês. Decisão do dono: rota, não seção.
                    Também do gestor, também dentro do LeadsProvider (sai dos leads
                    em memória: nenhuma consulta nova). */}
                <Route path="recebimentos/em-aberto" element={<RecebimentosEmAbertoPage />} />
                {/* VA-2: o HISTÓRICO é do gestor — é a conferência de quem
                    NÃO importou sobre o que o SDR importou. */}
                <Route path="recebimentos/importacoes" element={<ImportacoesPage />} />
                {/* VC — a folha do mês e as taxas de cada um: só do gestor. */}
                <Route path="remuneracao" element={<RemuneracaoPage />} />
                <Route path="remuneracao/config" element={<ConfigRemuneracaoPage />} />
              </Route>
            </Route>
            {/* Preferências fica FORA do LeadsProvider: não precisa da
                carteira de leads, e assiná-la aqui só custaria leitura. */}
            <Route
              path="vendas/preferencias"
              element={<PreferenciasPage espaco="vendas" />}
            />
          </Route>

          {/* Espaço Recepção */}
          <Route element={<RequireAuth papeis={RECEPCAO} />}>
            <Route path="pacientes" element={<PacientesPage />} />
            <Route path="confirmar-agenda" element={<ConfirmarAgendaPage />} />
            <Route
              path="recepcao/preferencias"
              element={<PreferenciasPage espaco="recepcao" />}
            />
          </Route>

          {/* A agenda é o ponto de encontro dos dois espaços: o SDR marca a
              consulta, a recepção acompanha o dia. */}
          <Route element={<RequireAuth papeis={TODOS_DA_CLINICA} />}>
            <Route path="agenda" element={<AgendaPage />} />
          </Route>

          {/* Central de conversas, um canal por rota (docs/plano-inbox.md).
              O canal escolhe o espaço e o recorte da lista; o provider
              barra quem não tem o papel e redireciona canal desconhecido.
              `/conversas` puro entra aqui e cai no padrão do papel, para
              não sobrar rota órfã. */}
          <Route element={<RequireAuth papeis={TODOS_DA_CLINICA} />}>
            <Route element={<ConversasProvider />}>
              <Route path="conversas" element={<ConversasPage />} />
              <Route path="conversas/:canal" element={<ConversasPage />} />
            </Route>
          </Route>

          {/* Administração da clínica */}
          <Route element={<RequireAuth papeis={GESTAO} />}>
            <Route path="configuracoes" element={<ConfiguracoesPage />} />
            <Route path="equipe" element={<EquipePage />} />
          </Route>

          {/* Administração do SaaS */}
          <Route element={<RequireAuth papeis={['superadmin']} />}>
            <Route path="admin" element={<AdminPage />} />
            {/* ★★★ WT lote 1 — Saúde do sistema: os erros que o navegador da equipe
                registrou. ⚠ SÓ superadmin, e FORA do menu: nem o gestor da clínica vê o
                item (as rules também negam a leitura a ele). */}
            <Route path="admin/saude" element={<SaudePage />} />
          </Route>

          <Route path="*" element={<NaoEncontradaPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RotasDoApp />
      </AuthProvider>
    </BrowserRouter>
  );
}
